/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useCallback, useEffect, useMemo, useState } from 'react';
// material
import Space from 'material/space/space';
import { CopySnippetField, Tip, SingleSelectDropDown, TextField, displayModal } from '@insticator/insticator-ui';
import { SectionGroupV2 } from 'material/expandablesection/v2'
// styles
import injectSheet from 'react-jss'
import styles from './placement_styles'
import { Button } from '@insticator/insticator-ui/dist/material';
import { useDispatch, useSelector } from 'react-redux';
import ControlBarModal from '../controlbarmodal/controlbarmodal';
import ButtonConfigModal from '../buttonconfigmodal/buttonconfigmodal';
import Dialog from '@mui/material/Dialog';
import { setTempProductManagerConfig } from 'state/slices/productmanager';
import { updateControlBarConfig, updateProductManagerConfig } from 'state/thunks/productmanager';

const layoutMap = {
  INLINE: {value: 'INLINE', name: 'Inline'},
  OVERLAY: {value: 'OVERLAY', name: 'Overlay'},
  SIDE_PANEL_LEFT: {value: 'SIDE_PANEL_LEFT', name: 'Side Panel (Left)'},
  SIDE_PANEL_RIGHT: {value: 'SIDE_PANEL_RIGHT', name: 'Side Panel (Right)'},
}

const layoutOptions = [layoutMap.INLINE, layoutMap.OVERLAY, layoutMap.SIDE_PANEL_LEFT, layoutMap.SIDE_PANEL_RIGHT]

const triggerMap = {
  CONTROL_BAR: {value: 'CONTROL_BAR', name: 'Control Bar'},
  BUTTON: {value: 'BUTTON', name: 'Button'},
  NONE: {value: 'NONE', name: 'None'},
  CUSTOM: {value: 'CUSTOM', name: 'Custom'}
}

const inlineTriggerOptions = [triggerMap.CONTROL_BAR, triggerMap.BUTTON, triggerMap.NONE]

const overlayPanelTriggerOptions = [triggerMap.CONTROL_BAR, triggerMap.BUTTON, triggerMap.CUSTOM]

const selectorModeMap = {
  CSS: {value: 'CSS', name: 'CSS Selector'},
  XPATH: {value: 'XPATH', name: 'XPath'}
}

const selectorModeOptions = [selectorModeMap.CSS, selectorModeMap.XPATH]

const placementModeMap = {
  DYNAMIC: {value: 'DYNAMIC', name: 'Dynamic'},
  HARDCODE: {value: 'HARDCODE', name: 'Hardcode'},
  HARDCODE_INFINITE: {value: 'HARDCODE_INFINITE', name: 'Hardcode - Infinite Scroll'}
}

const buttonPlacementModeOptions = [placementModeMap.DYNAMIC, placementModeMap.HARDCODE]
const placementModeOptions = [placementModeMap.DYNAMIC, placementModeMap.HARDCODE, placementModeMap.HARDCODE_INFINITE]

/* ========== ~~~~~~~~~~ PRODUCT MANAGER: SITE SETUP : FEATURES ~~~~~~~~~~ ========== */
const Placement = injectSheet(styles)((props) => {
  const dispatch = useDispatch()
  const { siteUUID } = useSelector(state => state.session);
  const { productManagerConfigChanges, productManagerConfig, tempProductManagerConfig } = useSelector(state => state.productManager)

  const [triggerOptions, setTriggerOptions] = useState(inlineTriggerOptions)

  const { 
      buttonConfig, 
      layout = 'INLINE', 
      trigger = 'BUTTON', 
      groupId, 
      infiniteScrollEnabled, 
      dynamicPlacement, 
      selectorMode, 
      selector, 
      urlFilter, 
      onChange, 
      alignment, 
      placement, 
      onFieldsChange,
      showCommenting,
      showTrendingNow,
      showArticleSentiment,
      products
    } = props

  const customTriggerCode = `Insticator.load('trigger-layout', { groupId: '${groupId}' })`
  const bodyCode = `<div class="insticator-product-group" data-insticator-group-id="${groupId}"></div><script>Insticator.load("insticator-product-group", { id: "${groupId}" })</script>`
  const bodyCodeInfiniteScrollEnabled = `<div class="insticator-product-group" data-insticator-group-id="${groupId}"></div>
  <script>Insticator.load("insticator-product-group", {
      id: "${groupId}",
      pageId: "{pageId}",
      pageUrl: "{pageUrl}",
      pageTitle: "{pageTitle}"
    })
  </script>`

  const selectedPlacementMode = dynamicPlacement ? 'DYNAMIC': (infiniteScrollEnabled ? 'HARDCODE_INFINITE' : 'HARDCODE')

  const placementTip = useMemo(() => {
    if(selectedPlacementMode === 'DYNAMIC') {
      return  <Tip 
        space={'none'}
        icon={'question-circle'} 
        content={`
          Insert a group of products into a specific location on your site dynamically by filling out the fields below. 
          No body code will need to be integrated manually in this case.
        `} 
        label={''} 
      />
    }
    if(selectedPlacementMode === 'HARDCODE') {
      return <Tip 
        space={'none'} 
        icon={'question-circle'} 
        content={
          <span>
          <p>
            Place this code directly into the {'<body>'} element of your page where you want this product group to show. 
            Use this option in the event of a custom site integration or if there are concerns about dynamic product insertion.
          </p>
          {!groupId ? <><br /><p>Save your draft to access the body code.</p></> : ''}
          </span>
        } 
        label={''} 
      />
    }
    if(selectedPlacementMode === 'HARDCODE_INFINITE') {
      return <Tip 
        space={'none'} 
        icon={'question-circle'} 
        content={
          <span>
          <p>
            Place this code directly into the {'<body>'} element of your page where you want this product group to show. 
            Pass data for page id, title, and URL to ensure infinite scroll capability. 
            This body code is slightly different than the standard hardcode. Infinite scroll setups cannot use dynamic product insertion.
          </p>
          {!groupId ? <><br /><p>Save your draft to access the body code.</p></> : ''}
          </span>
        } 
        label={''} 
      />
    }
  }, [selectedPlacementMode])

  useEffect(() => {
    // Change trigger if it is not applicable to the selected layout. Hard fix for single dropdown not updating the props
    setTriggerOptions([])
  }, [layout])

  useEffect(() => {
    if(triggerOptions.length === 0 ) {
      setTriggerOptions(layout === 'INLINE' ? inlineTriggerOptions : overlayPanelTriggerOptions)
      if(layout === 'INLINE' && trigger === 'CUSTOM') onChange('trigger', 'BUTTON')
      if(layout !== 'INLINE' && trigger === 'NONE') onChange('trigger', 'CUSTOM')
    }
  }, [triggerOptions])

  const showControlBar = () => {
    setIsControlBarOpen(true)
  }

  const applyButtonConfigs = (config) => {
    onFieldsChange({
      buttonConfig: {
        ...buttonConfig,
        ...config
      }
    })
    setIsButtonConfigOpen(false)
  }

  const applyControlBarConfig = (config) => {
    onFieldsChange({
      controlBarConfig: {
        ...config
      }
    })
  }
  
  const showButtonConfigModal = () => setIsButtonConfigOpen(true)

  const showIntegration = useMemo(() => layout === 'INLINE' || trigger === 'BUTTON', [layout, trigger])

  const showOverlaySidePanelCode = useMemo(() => layout !== 'INLINE' && trigger === 'CUSTOM' ,[layout, trigger])

  const [isControlBarOpen, setIsControlBarOpen] = useState(false)
  const [isButtonConfigOpen, setIsButtonConfigOpen] = useState(false)

  const noProducts = !showCommenting && !showTrendingNow && !showArticleSentiment

  return (
    <SectionGroupV2 
      klass={`placement ${props.classes.placement}`} 
      title={'Placement'} 
      description={'Define where these products will display on your web page.'}
      direction={'column'}
    >
      <Dialog fullWidth open={isControlBarOpen}>
        <ControlBarModal
          showCommenting={showCommenting}
          showTrendingNow={showTrendingNow}
          showArticleSentiment={showArticleSentiment}
          products={products}
          onCancel={() => setIsControlBarOpen(false)} 
          onSave={(config) => {
            applyControlBarConfig(config)
            setIsControlBarOpen(false)
          }} 
        />
      </Dialog>
      <Dialog fullWidth open={isButtonConfigOpen}>
        <ButtonConfigModal
          primaryColor={buttonConfig?.primaryColor}
          enableMonochrome={buttonConfig?.enableMonochrome}
          text={buttonConfig?.text}
          iconStyle={buttonConfig?.iconStyle}
          fontTypeface={buttonConfig?.fontTypeface}
          alignment={buttonConfig?.alignment} 
          buttonSize={buttonConfig?.buttonSize}
          onCancel={() => setIsButtonConfigOpen(false)} 
          onSave={configs =>  applyButtonConfigs(configs)} 
        />
      </Dialog>
      <Space direction={'column'}>
        <Space alignItems={'center'}>
          <span className='label'>Layout</span> 
          <Tip 
            space={'none'}
            icon={'question-circle'} 
            content={`
                Select how products will display on the site. 
                Directly inline under the article content, in a panel that flies out from the side of the browser window, 
                or in an overlay on top of the site content.
            `} 
            label={''} 
          />
          <SingleSelectDropDown 
            name={layoutMap[layout || 'INLINE'].name} 
            searchable={false} 
            onSelect={opt =>  onChange('layout', opt.value)} 
            items={layoutOptions}
          />
        </Space>

        <Space alignItems={'center'}>
          <span className='label'>Trigger</span>
          <Tip 
            space={'none'}
            icon={'question-circle'} 
            content={`
                Select how the user can trigger the showing of products. 
                User can click a button or interact with a control bar to launch the products.
            `} 
            label={''} 
          />
          <SingleSelectDropDown 
            klass={'menuItems'}
            name={triggerMap[trigger || 'BUTTON'].name} 
            searchable={false} 
            onSelect={opt => onChange('trigger', opt.value)} 
            items={triggerOptions}
          />
        {(trigger === 'BUTTON' || trigger === 'CONTROL_BAR') &&
         <Button 
          name={'configure'} 
          type={'action'}
          variant={'none'} 
          color={'light'} 
          text={'Configure'} 
          disabled={noProducts}
          handleClick={() => {
            if(trigger === 'CONTROL_BAR') showControlBar()
            if(trigger === 'BUTTON') showButtonConfigModal()
          }} 
        />
        }
        </Space>
        <Space>
          <span className='label'></span>
          {!tempProductManagerConfig.controlBarConfig && trigger === 'CONTROL_BAR' && (
              <span className='errormessage' style={{ marginLeft: '20px'}}>Please save Control Bar configurations first before testing or publishing this group.</span>
          )}
          {!buttonConfig && trigger === 'BUTTON' && (
              <span className='errormessage' style={{ marginLeft: '20px'}}>Please save Button configurations first before testing or publishing this group.</span>
          )}
        </Space>
       {showIntegration && (
         <Space alignItems={'center'}>
          <span className='label'>Integration</span> {placementTip}
          <SingleSelectDropDown 
            name={placementModeMap[selectedPlacementMode || 'DYNAMIC'].name} 
            searchable={false} 
            onSelect={opt => {
              if(opt.value === 'DYNAMIC') onChange('dynamicPlacement', true)
              if(opt.value === 'HARDCODE') onChange('infiniteScrollEnabled', false)
              if(opt.value === 'HARDCODE_INFINITE') onChange('infiniteScrollEnabled', true)
            }} 
            items={trigger === 'BUTTON' && layout !== 'INLINE' ? buttonPlacementModeOptions : placementModeOptions}
          />
        </Space>
       )}

       {showOverlaySidePanelCode && (
        <Space klass={`disabled-${!groupId}`} alignItems={'center'}>
          <span className='label'>Code</span>
          <div style={{width: '18px'}}></div>
          <CopySnippetField
            codeSnippet={customTriggerCode}
            inline
          />
        </Space>
       )}
      
    {(layout === 'INLINE' || trigger === 'BUTTON') && (
      !dynamicPlacement ?
        <Space klass={`disabled-${!groupId}`} alignItems={'center'}>
          <span className='label'>Code</span>
          <div style={{width: '18px'}}></div>
          <CopySnippetField
            codeSnippet={infiniteScrollEnabled ? bodyCodeInfiniteScrollEnabled : bodyCode}
            inline
          />
        </Space>
        :
        <>
        <Space alignItems={'center'}>
          <span className='label'>Location</span> 
          <Tip 
            space={'none'} 
            icon={'question-circle'} 
            content={`
              Get the CSS selector or XPath code and paste it below. Navigate to your site. Then click on any article. 
              Find the placement in that article you want the product grouping to appear. Right click and “Inspect”. 
              Copy the CSS selector or XPath code of that element and paste it here.              
            `} 
            label={''} 
          />
          <SingleSelectDropDown 
            name={selectorModeMap[selectorMode || 'CSS'].name} 
            searchable={false} 
            onSelect={opt => onChange('selectorMode', opt.value)} 
            items={selectorModeOptions}
          />
          <TextField 
            type={`text`} 
            name={`selector`} 
            value={selector} 
            handleChange={e => { onChange?.('selector', e.target.value)}} 
            valid={true} 
            label={''}
            placeholder={`${selectorMode === 'CSS' ? 'Enter css selector' : 'Enter xPath selector'}`} 
            maxLength={256}
          />
        </Space>
        <Space alignItems={'center'}>
        <span className='label'></span> 
          <Tip 
            space={'none'} 
            icon={'question-circle'} 
            content={
              <span>Add a <a target="_blank" href="https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_expressions" rel="noreferrer">regular expression</a> to 
              target the specific pages you want this product to be inserted on. 
              Use this <a target='_blank' href="https://regexr.com/" rel="noreferrer">helper tool</a> to test your expressions.
              </span>
            } 
            label={''} 
          />
          <TextField 
            type={`text`} 
            name={`urlfilter`} 
            value={urlFilter} 
            handleChange={e => { onChange?.('urlFilter', e.target.value)}} 
            valid={true} 
            label={''}
            placeholder={`Enter expression`} 
            maxLength={512}
          />

        </Space>
        </>
        
       )}

       {layout !== 'INLINE' && trigger === 'CONTROL_BAR' && (
          <Space>
            <span className='label'></span> 
            <div style={{width: '18px'}}></div>
            <TextField 
              type={`text`} 
              name={`urlfilter`} 
              value={urlFilter} 
              handleChange={e => { onChange?.('urlFilter', e.target.value)}} 
              valid={true} 
              label={''}
              placeholder={`Enter expression`} 
              maxLength={512}
            />
          </Space>
       )}

      </Space>
    </SectionGroupV2>
  )
})

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default Placement;